import React from 'react'
import PropTypes from 'prop-types'

/**
 * Modal component that displays when session is about to timeout
 * @param {Object} props - Component props
 * @param {boolean} props.isVisible - Whether the modal is visible
 * @param {string} props.remainingTime - Formatted remaining time string
 * @param {Function} props.onExtendSession - Callback to extend the session
 * @param {Object} props.config - Configuration object for styling and text
 */
const IdleTimeoutModal = ({ isVisible, remainingTime, onExtendSession, config = {} }) => {
    if (!isVisible) {
        return null
    }

    const {
        title = 'Session Timeout Warning',
        message = 'Your session will expire soon due to inactivity.',
        extendButtonText = 'Stay Logged In',
        primaryColor = '#007bff',
        backgroundColor = '#ffffff',
        textColor = '#333333',
        overlayColor = 'rgba(0, 0, 0, 0.5)',
    } = config

    const modalStyles = {
        overlay: {
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: overlayColor,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
        },
        modal: {
            backgroundColor: backgroundColor,
            borderRadius: '8px',
            padding: '24px',
            maxWidth: '400px',
            width: '90%',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)',
            color: textColor,
        },
        header: {
            marginBottom: '16px',
        },
        title: {
            fontSize: '18px',
            fontWeight: 'bold',
            margin: '0 0 8px 0',
            color: textColor,
        },
        icon: {
            fontSize: '48px',
            textAlign: 'center',
            marginBottom: '16px',
            color: primaryColor,
        },
        message: {
            fontSize: '14px',
            lineHeight: '1.5',
            margin: '0 0 16px 0',
            color: textColor,
        },
        timeDisplay: {
            fontSize: '24px',
            fontWeight: 'bold',
            textAlign: 'center',
            margin: '16px 0',
            padding: '12px',
            backgroundColor: '#f8f9fa',
            borderRadius: '4px',
            color: primaryColor,
        },
        buttonContainer: {
            display: 'flex',
            gap: '12px',
            marginTop: '20px',
            placeContent: 'center',
        },
        primaryButton: {
            backgroundColor: primaryColor,
            color: '#ffffff',
            border: 'none',
            borderRadius: '4px',
            padding: '10px 20px',
            fontSize: '14px',
            fontWeight: '500',
            cursor: 'pointer',
            transition: 'background-color 0.2s',
        },
    }

    const handleExtendSession = () => {
        if (onExtendSession) {
            onExtendSession()
        }
    }

    return (
        <div style={modalStyles.overlay} data-testid="idle-timeout-modal">
            <div style={modalStyles.modal} role="dialog" aria-modal="true" aria-labelledby="timeout-title">
                <div style={modalStyles.icon}>⏳</div>
                <div style={modalStyles.header}>
                    <h2 id="timeout-title" style={modalStyles.title}>
                        {title}
                    </h2>
                    <p style={modalStyles.message}>{message}</p>
                </div>

                <div style={modalStyles.timeDisplay} data-testid="remaining-time">
                    Time remaining: {remainingTime}
                </div>

                <div style={modalStyles.buttonContainer}>
                    <button
                        style={modalStyles.primaryButton}
                        onClick={handleExtendSession}
                        data-testid="extend-session-button"
                        onMouseOver={e => {
                            e.target.style.backgroundColor = '#0056b3'
                        }}
                        onMouseOut={e => {
                            e.target.style.backgroundColor = primaryColor
                        }}
                    >
                        {extendButtonText}
                    </button>
                </div>
            </div>
        </div>
    )
}

IdleTimeoutModal.propTypes = {
    isVisible: PropTypes.bool.isRequired,
    remainingTime: PropTypes.string.isRequired,
    onExtendSession: PropTypes.func.isRequired,
    config: PropTypes.shape({
        title: PropTypes.string,
        message: PropTypes.string,
        extendButtonText: PropTypes.string,
        primaryColor: PropTypes.string,
        backgroundColor: PropTypes.string,
        textColor: PropTypes.string,
        overlayColor: PropTypes.string,
    }),
}

export default IdleTimeoutModal
